import React from 'react';
import PropTypes from "prop-types";
import dayjs from "dayjs";
import entete from "../../../assets/img/entete.jpg";
import { frenchDateOnly } from '../../../variables/functions';
import { displayNumbers } from '../../../variables/numbersFunctions';
import { DocumentFooter } from '../../../components/docs/DocumentFooter';

const BorderauReversementCompagnie = React.forwardRef((props, ref) => {
    const emissions = props.emissions || [];

    // ── Période : le FILTRE prime s'il est défini ; sinon déduite des dates de règlement ──
    const reglees = emissions.filter((e) => e.dateReglement);
    const sorted = [...reglees].sort((a, b) => new Date(a.dateReglement) - new Date(b.dateReglement));
    const minStr = sorted[0]?.dateReglement;
    const maxStr = sorted[sorted.length - 1]?.dateReglement;
    const sameDay = minStr && maxStr && new Date(minStr).toDateString() === new Date(maxStr).toDateString();
    const periodeCalculee = minStr
        ? (sameDay ? frenchDateOnly(minStr) : `${frenchDateOnly(minStr)} – ${frenchDateOnly(maxStr)}`)
        : "";
    const periodeAuto = props.periode ? props.periode : periodeCalculee;
    // PRIORITÉ à la période SÉLECTIONNÉE par l'utilisateur (persistée sur le bordereau ou choisie
    // dans l'aperçu) ; les anciens bordereaux sans période stockée retombent sur le min/max.
    const periodeDuAu = (props.periodeDebut && props.periodeFin)
        ? `PÉRIODE DU ${frenchDateOnly(props.periodeDebut)} AU ${frenchDateOnly(props.periodeFin)}`
        : (minStr
            ? `PÉRIODE DU ${frenchDateOnly(minStr)} AU ${frenchDateOnly(maxStr)}`
            : (periodeAuto ? `PÉRIODE : ${periodeAuto}` : ""));
    const periodeTexte = (props.periodeDebut && props.periodeFin)
        ? `${frenchDateOnly(props.periodeDebut)} – ${frenchDateOnly(props.periodeFin)}`
        : periodeAuto;

    // ── Montants (le montant du règlement est TTC) ──
    // Prime nette totale = TTC − taxes − accessoires. Elle se répartit en PN Pool + PN Cie :
    //   PN Cie  = part des garanties qui NE relèvent PAS du Pool (prime_nette_compagnie, exposée par
    //             /api/reglement/a-reverser). Naguère figée à 0 → part compagnie invisible.
    //   PN Pool = prime nette totale − PN Cie.
    // L'ancienne formule PN Pool « (montant − accessoires)/1.06 » était en plus FAUSSE : les taxes
    // portent sur (PN + accessoires), donc soustraire les accessoires AVANT de diviser par 1,06
    // gonflait la PN (ex. (211525−18000)/1.06 = 182 570 au lieu de 181 552). On dérive des taxes/
    // accessoires réels → garantit PN Pool + PN Cie + accessoires + taxes = TTC.
    const primeNetteTotaleOf = (c) => (c.montant || 0) - (c.taxes || 0) - (c.accessoires || 0);
    const pnCieOf = (c) => (c.primeNetteCompagnie || 0);
    const pnPoolOf = (c) => primeNetteTotaleOf(c) - pnCieOf(c);
    const commissionOf = (c) => (c.commission != null ? c.commission : ((c.montant || 0) * (c.tauxCommission || 0) / 100));
    const netOf = (c) => (c.montant || 0) - commissionOf(c);

    // ── Totaux ──
    const totalPNPool = emissions.reduce((s, c) => s + pnPoolOf(c), 0);
    const totalPNCie = emissions.reduce((s, c) => s + pnCieOf(c), 0);
    const totalAccessoires = emissions.reduce((s, c) => s + (c.accessoires || 0), 0);
    const totalTaxes = emissions.reduce((s, c) => s + (c.taxes || 0), 0);
    const totalPTTC = emissions.reduce((s, c) => s + (c.montant || 0), 0);
    const totalCommission = emissions.reduce((s, c) => s + commissionOf(c), 0);
    // Net à reverser à la compagnie = primes TTC encaissées − commissions retenues
    const totalAReverser = totalPTTC - totalCommission;

    // ── Règlement du bordereau (affiché uniquement s'il est réglé) ──
    const paiement = props.paiement || {};
    const isRegle = !!(paiement.dateReglement || (paiement.modePaiement && paiement.modePaiement !== "-"));
    const modePaiementDetaille = (() => {
        const m = paiement.modePaiement;
        const num = paiement.numeroPaiement;
        const banque = paiement.banque;
        switch (m) {
            case "ESPECES":
            case "ESPECE":
                return "Espèces";
            case "BANQUE":
                return `Banque (Chèque)${num ? ` — Chèque n° ${num}` : ""}${banque ? ` — ${banque}` : ""}`;
            case "VIREMENT":
                return `Virement bancaire${num ? ` — Réf. ${num}` : ""}${banque ? ` — ${banque}` : ""}`;
            case "MOBILE_MONEY":
                return `Mobile Money (Mixx By Yas / Flooz)${num ? ` — N° ${num}` : ""}`;
            case "COMPENSATION":
                return "Compensation";
            case "RISTOURNE":
                return "Ristourne";
            default:
                return m || "—";
        }
    })();

    // ── Styles monochromes (impression noir & blanc) ──
    const border = "1px solid #000";
    const th = {
        border, padding: "5px 6px", fontSize: 11, fontWeight: 700, textAlign: "left",
        background: "#e8e8e8", WebkitPrintColorAdjust: "exact", printColorAdjust: "exact",
    };
    const thR = { ...th, textAlign: "right" };
    const td = { border, padding: "4px 6px", fontSize: 11, color: "#000", verticalAlign: "top" };
    const tdR = { ...td, textAlign: "right", whiteSpace: "nowrap" };
    const tdC = { ...td, textAlign: "center" };
    const tot = { ...tdR, fontWeight: 700, background: "#f2f2f2", WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" };

    return (
        <div ref={ref} className="brd-reversement" style={{ color: "#000" }}>
            <style>{`
                .brd-reversement .brd-page {
                    display: flex;
                    flex-direction: column;
                    width: 100%;
                    max-width: 1100px;
                    margin: 0 auto;
                    min-height: 78vh;
                }
                .brd-reversement .brd-spacer { flex: 1 1 auto; }
                @media print {
                    @page { size: A4 landscape; margin: 8mm; }
                    html, body { margin: 0; height: auto; }
                    .brd-reversement { color: #000 !important; }
                    .brd-reversement .brd-page { min-height: 192mm; max-width: none; }
                    .brd-reversement table { page-break-inside: auto; }
                    .brd-reversement tr { page-break-inside: avoid; page-break-after: auto; }
                    .brd-reversement thead { display: table-header-group; }
                    .brd-reversement .brd-footer { page-break-inside: avoid; }
                }
            `}</style>

            <div className="brd-page">
                {/* En-tête : logo de la compagnie (si disponible) + en-tête du Pool,
                    comme sur les autres documents de l'application. */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                    {props.logoCompagnie ? (
                        <img
                            src={props.logoCompagnie}
                            alt="Logo compagnie"
                            style={{ width: "50%", maxHeight: 120, objectFit: "contain" }}
                        />
                    ) : null}
                    <img
                        src={entete}
                        alt="En-tête"
                        style={{ width: props.logoCompagnie ? "50%" : "100%", maxHeight: 120, objectFit: "contain" }}
                    />
                </div>

                {/* Titre : N° de bordereau + période */}
                <div style={{ textAlign: "center", margin: "10px 0 6px" }}>
                    <div style={{ fontSize: 16, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3 }}>
                        BORDEREAU DE REVERSEMENT N° {props.numeroBordereau || "—"}
                    </div>
                    {periodeDuAu ? (
                        <div style={{ fontSize: 13, fontWeight: 600, marginTop: 2 }}>{periodeDuAu}</div>
                    ) : null}
                    <div style={{ borderBottom: "2px solid #000", width: 90, margin: "6px auto 0" }} />
                </div>

                {/* Contenu tabulaire avec marges gauche/droite */}
                <div style={{ padding: "0 18px" }}>
                {/* Bloc d'informations */}
                <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 8, fontSize: 11 }}>
                    <tbody>
                        <tr>
                            <td style={{ padding: "2px 6px", width: "50%" }}>
                                <b>Versé par :</b> {props.reverseur || "—"}
                            </td>
                            <td style={{ padding: "2px 6px", width: "50%", textAlign: "right" }}>
                                <b>N° Bordereau :</b> {props.numeroBordereau || "—"}
                            </td>
                        </tr>
                        <tr>
                            <td style={{ padding: "2px 6px" }}><b>Période :</b> {periodeTexte || "—"}</td>
                            <td style={{ padding: "2px 6px", textAlign: "right" }}>
                                <b>Nombre de règlements :</b> {emissions.length}
                            </td>
                        </tr>
                    </tbody>
                </table>

                {/* Détail des primes à reverser */}
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                        <tr>
                            <th style={{ ...th, width: 26, textAlign: "center" }}>N°</th>
                            <th style={th}>Police</th>
                            <th style={{ ...th, textAlign: "center" }}>Av.</th>
                            <th style={th}>Assuré</th>
                            <th style={{ ...th, textAlign: "center" }}>Effet</th>
                            <th style={{ ...th, textAlign: "center" }}>Échéance</th>
                            <th style={{ ...th, textAlign: "center" }}>Cat.</th>
                            <th style={thR}>PN Pool</th>
                            <th style={thR}>PN Cie</th>
                            <th style={thR}>Access.</th>
                            <th style={thR}>Taxes</th>
                            <th style={thR}>P.TTC</th>
                            <th style={thR}>Taux</th>
                            <th style={thR}>Commission</th>
                            <th style={{ ...thR, width: 110 }}>Net à reverser</th>
                        </tr>
                    </thead>
                    <tbody>
                        {emissions.map((r, i) => (
                            <tr key={r.codeId || i}>
                                <td style={tdC}>{i + 1}</td>
                                <td style={td}>{r.numeroPolice}</td>
                                <td style={tdC}>{r.avenant || "AN"}</td>
                                <td style={td}>{r.nomSouscripteur} {r.prenomSouscripteur || ""}</td>
                                <td style={tdC}>{r.dateEffet ? frenchDateOnly(r.dateEffet) : "-"}</td>
                                <td style={tdC}>{r.dateEcheance ? frenchDateOnly(r.dateEcheance) : "-"}</td>
                                <td style={tdC}>{r.categorie || "-"}</td>
                                <td style={tdR}>{displayNumbers(pnPoolOf(r))}</td>
                                <td style={tdR}>{displayNumbers(pnCieOf(r))}</td>
                                <td style={tdR}>{displayNumbers(r.accessoires || 0)}</td>
                                <td style={tdR}>{displayNumbers(r.taxes || 0)}</td>
                                <td style={tdR}>{displayNumbers(r.montant || 0)}</td>
                                <td style={tdR}>{displayNumbers(r.tauxCommission || 0)} %</td>
                                <td style={tdR}>{displayNumbers(commissionOf(r))}</td>
                                <td style={{ ...tdR, width: 110, fontWeight: 600 }}>{displayNumbers(netOf(r))}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td style={{ ...tot, textAlign: "left" }} colSpan={7}>TOTAUX ({emissions.length} règlement(s))</td>
                            <td style={tot}>{displayNumbers(Math.trunc(totalPNPool))}</td>
                            <td style={tot}>{displayNumbers(Math.trunc(totalPNCie))}</td>
                            <td style={tot}>{displayNumbers(Math.trunc(totalAccessoires))}</td>
                            <td style={tot}>{displayNumbers(Math.trunc(totalTaxes))}</td>
                            <td style={tot}>{displayNumbers(Math.trunc(totalPTTC))}</td>
                            <td style={tot}>—</td>
                            <td style={tot}>{displayNumbers(Math.trunc(totalCommission))}</td>
                            <td style={{ ...tot, width: 110 }}>{displayNumbers(Math.trunc(totalAReverser))}</td>
                        </tr>
                    </tfoot>
                </table>

                {/* Bloc « Règlement » (si réglé) à gauche + récapitulatif à droite */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginTop: 12, gap: 16 }}>
                    {isRegle ? (
                        <table style={{ borderCollapse: "collapse", fontSize: 12, alignSelf: "flex-end" }}>
                            <tbody>
                                <tr>
                                    <td colSpan={2} style={{ border, padding: "5px 10px", fontWeight: 700, background: "#e8e8e8", WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}>
                                        Règlement du bordereau
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ border, padding: "5px 10px", fontWeight: 600 }}>Date de règlement</td>
                                    <td style={{ border, padding: "5px 10px", whiteSpace: "nowrap" }}>
                                        {paiement.dateReglement ? frenchDateOnly(paiement.dateReglement) : "—"}
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ border, padding: "5px 10px", fontWeight: 600 }}>Mode de paiement</td>
                                    <td style={{ border, padding: "5px 10px" }}>{modePaiementDetaille}</td>
                                </tr>
                                {paiement.commentaireEncaissement ? (
                                    <tr>
                                        <td style={{ border, padding: "5px 10px", fontWeight: 600 }}>Observation</td>
                                        <td style={{ border, padding: "5px 10px", whiteSpace: "pre-wrap", maxWidth: 260 }}>
                                            {paiement.commentaireEncaissement}
                                        </td>
                                    </tr>
                                ) : null}
                            </tbody>
                        </table>
                    ) : <div />}
                    <table style={{ borderCollapse: "collapse", fontSize: 12 }}>
                        <tbody>
                            <tr>
                                <td style={{ border, padding: "5px 10px", fontWeight: 600 }}>Total primes TTC</td>
                                <td style={{ border, padding: "5px 10px", textAlign: "right", width: 140, whiteSpace: "nowrap" }}>
                                    {displayNumbers(Math.trunc(totalPTTC))} F CFA
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                </div>{/* /Contenu tabulaire */}

                {/* Espaceur : pousse le pied de page en bas de la page */}
                <div className="brd-spacer" />

                {/* Signatures : l'intermédiaire et, pour la société, l'ordonnateur et le caissier */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", margin: "24px 18px 12px", fontSize: 12 }}>
                    <div style={{ textAlign: "center", width: "30%" }}>
                        <div style={{ fontWeight: 700, marginBottom: 46 }}>L'intermédiaire</div>
                        <div style={{ borderTop: "1px solid #000", paddingTop: 2 }}>(signature)</div>
                    </div>
                    <div style={{ width: "60%" }}>
                        <div style={{ fontWeight: 700, textAlign: "center", marginBottom: 6 }}>Pour la société</div>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <div style={{ textAlign: "center", width: "46%" }}>
                                <div style={{ marginBottom: 44 }}>L'Ordonnateur</div>
                                <div style={{ borderTop: "1px solid #000", paddingTop: 2 }}>(signature)</div>
                            </div>
                            <div style={{ textAlign: "center", width: "46%" }}>
                                <div style={{ marginBottom: 44 }}>Le Caissier</div>
                                <div style={{ borderTop: "1px solid #000", paddingTop: 2 }}>(signature)</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="brd-footer">
                    {/* Date et heure d'impression, même format et même emplacement que sur les
                        Conditions Particulières : le bordereau est une pièce comptable remise à
                        la compagnie, l'exemplaire papier doit porter son horodatage. */}
                    <p style={{ marginLeft: "15px" }}>
                        Imprimé le : {dayjs().format("DD/MM/YYYY HH:mm:ss")}
                    </p>
                    <DocumentFooter />
                </div>
            </div>
        </div>
    );
});

BorderauReversementCompagnie.propTypes = {
    emissions: PropTypes.array,
    numeroBordereau: PropTypes.string,
    periode: PropTypes.string,
    periodeDebut: PropTypes.string,
    periodeFin: PropTypes.string,
    reverseur: PropTypes.string,
    paiement: PropTypes.object,
    logoCompagnie: PropTypes.string,
};

export default BorderauReversementCompagnie;
